<template>
  <v-card :tile="true" class="rounded-b" :class="{ 'rounded-t': !matrixHasAlertMessage }">
    <v-data-table
      :headers="headers"
      :items="report | addKey"
      item-key="key"
      multi-sort
      show-expand
      :loading="isLoading"
      :single-expand="true"
      sort-by="purchaseCondition.rowIndex"
    >
      <template v-slot:[`item.field`]="{ value }">
        {{ getCorrespondingMatrixHeader(value) }}
      </template>
      <template v-slot:[`item.purchaseCondition.circuit`]="{ value }">
        {{ $t(`headers.circuits.${value}`) }}
      </template>
      <template v-slot:[`item.purchaseCondition`]="{ value, item }">
        {{ getRejectedValue(value, item.field) }}
      </template>
      <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
        <v-btn
          v-if="couldBeAndIsNotExpanded(item, isExpanded)"
          role="expand"
          icon
          color="primary"
          @click="expand(true)"
        >
          <v-icon dark>mdi-chevron-down-circle-outline</v-icon>
        </v-btn>
        <v-btn v-if="couldBeAndIsExpanded(item, isExpanded)" role="foldup" icon color="primary" @click="expand(false)">
          <v-icon dark>mdi-chevron-up-circle-outline</v-icon>
        </v-btn>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-chip
            class="ma-2"
            color="primary"
            outlined
            v-for="(value, index) in item.acceptedValues"
            v-bind:key="index"
          >
            {{ value }}
          </v-chip>
        </td>
      </template>
    </v-data-table>

    <v-btn v-if="isDownloadable" color="primary" role="download" fab dark absolute top right @click="download">
      <v-icon>mdi-file-download-outline</v-icon>
    </v-btn>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue';
import { TranslateResult } from 'vue-i18n';
import ReportHeaders from '@/components/control-report/control-report-headers.json';
import { MatrixFormatError } from '@/models/matrix-format-error';
import { UntypedPurchaseCondition } from '@/models/purchase-condition/untyped-purchase-condition';
import { addKeyToMatrixFormatErrors } from '@/filters/matrix-format-error-key';
import { accessValue, getControlReportFieldTranslateKey } from '@/services/purchase-conditions';
import { csvContent, csvFileName, hasStatusMessage, isDownloadable, matrix } from '@/modules/matrix';
import { downloadTypedStringContent } from '@/services/download';

export default Vue.extend({
  filters: {
    addKey: addKeyToMatrixFormatErrors
  },
  props: {
    matrixIndex: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      headers: ReportHeaders
    };
  },
  computed: {
    isLoading(): boolean {
      return matrix.isLoading;
    },
    report(): MatrixFormatError[] {
      return matrix.elements[this.matrixIndex].report;
    },
    isDownloadable(): boolean {
      return isDownloadable(this.matrixIndex);
    },
    matrixHasAlertMessage(): boolean {
      return hasStatusMessage(this.matrixIndex);
    }
  },
  methods: {
    couldBeAndIsExpanded(item: MatrixFormatError, isExpanded: boolean): boolean {
      return item.acceptedValues != null && isExpanded;
    },
    couldBeAndIsNotExpanded(item: MatrixFormatError, isExpanded: boolean): boolean {
      return item.acceptedValues != null && !isExpanded;
    },
    download() {
      const fileName = csvFileName(this.matrixIndex);
      const content = csvContent(this.matrixIndex);
      downloadTypedStringContent(fileName, content, 'text/csv');
    },
    getCorrespondingMatrixHeader(field: string | null): TranslateResult {
      if (field) {
        return this.$t(getControlReportFieldTranslateKey(field));
      }
      return '';
    },
    getRejectedValue(purchaseCondition: UntypedPurchaseCondition, fieldPath: string | null): string {
      if (fieldPath != null) {
        return accessValue(purchaseCondition, fieldPath);
      }
      return '';
    }
  }
});
</script>
